// SPDX-License-Identifier: SEE LICENSE
//
// ActivityLog — timeline-style right column, dark theme.
// Every line corresponds to a real backend event. No fabricated lines.

import { useEffect, useState } from 'react';

import { PILLARS } from '../lib/pillars';
import type { ActivityLine } from '../lib/useSessionStream';

function ago(now: number, ts: number): string {
  const ms = Math.max(0, now - ts);
  if (ms < 1000) return 'just now';
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  return `${Math.floor(ms / 3_600_000)}h ago`;
}

const TYPE_COLOR: Record<ActivityLine['type'], { dot: string; tag: string; tagBg: string; bordered: boolean }> = {
  'session.started':    { dot: '#15ddc9', tag: '#0a1812', tagBg: '#15ddc9', bordered: false },
  'session.restored':   { dot: '#15ddc9', tag: '#0a1812', tagBg: '#15ddc9', bordered: false },
  'session.finalized':  { dot: '#8aa0a6', tag: '#cfe0e0', tagBg: 'transparent', bordered: true },
  'step.started':       { dot: '#15ddc9', tag: '#15ddc9', tagBg: 'transparent', bordered: true },
  'step.passed':        { dot: '#34d399', tag: '#34d399', tagBg: 'transparent', bordered: true },
  'step.concern':       { dot: '#f5bf45', tag: '#f5bf45', tagBg: 'transparent', bordered: true },
  'step.failed':        { dot: '#f06a5e', tag: '#f06a5e', tagBg: 'transparent', bordered: true },
  'step.skipped':       { dot: '#6f878e', tag: '#9fb3b8', tagBg: 'transparent', bordered: true },
  'verdict.signed':     { dot: '#34d399', tag: '#34d399', tagBg: 'transparent', bordered: true },
  'telemetry.burst':    { dot: '#5d7178', tag: '#9fb3b8', tagBg: 'transparent', bordered: true },
  'certificate.minted': { dot: '#f5bf45', tag: '#0a0f18', tagBg: '#f5bf45', bordered: false },
};

const TYPE_TAG_LABEL: Record<ActivityLine['type'], string> = {
  'session.started': 'session',
  'session.restored': 'session',
  'session.finalized': 'session',
  'step.started': 'step',
  'step.passed': 'pass',
  'step.concern': 'concern',
  'step.failed': 'fail',
  'step.skipped': 'skip',
  'verdict.signed': 'signed',
  'telemetry.burst': 'frames',
  'certificate.minted': 'cert',
};

function tagLabel(l: ActivityLine): string {
  if (l.kind) {
    return PILLARS.find((p) => p.kind === l.kind)?.code ?? l.kind.toUpperCase();
  }
  return TYPE_TAG_LABEL[l.type];
}

const SENTINEL_HEADLINE_RE = /no run recorded|awaiting|no capture|verdict withheld/i;
function downgradeIfSentinel(line: ActivityLine): ActivityLine {
  if (line.type !== 'step.passed') return line;
  const h = line.headline;
  if (!h || SENTINEL_HEADLINE_RE.test(h)) {
    return {
      ...line,
      type: 'step.concern',
      headline: h && !SENTINEL_HEADLINE_RE.test(h) ? h : 'No real verdict — sentinel pass discarded',
    };
  }
  return line;
}

export function ActivityLog({ lines }: { lines: ActivityLine[] }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const h = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(h);
  }, []);

  return (
    <div
      style={{
        border: '1px solid rgba(120,160,160,0.16)',
        borderRadius: 14,
        background: 'rgba(9,16,26,0.55)',
        padding: '15px 16px',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 9rem)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 10, letterSpacing: 1.3, color: '#9fb3b8', fontWeight: 600 }}>
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: '#15ddc9',
              boxShadow: '0 0 6px #15ddc9',
            }}
            className="animate-livepulse"
          />
          LIVE ACTIVITY LOG
        </span>
        <span style={{ fontSize: 11, color: '#5d7178', fontFamily: "'JetBrains Mono', monospace" }}>{lines.length}</span>
      </div>

      {lines.length === 0 ? (
        <div
          className="animate-livepulse"
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '64px 24px',
            textAlign: 'center',
            fontSize: 12,
            color: '#5d7178',
          }}
        >
          waiting for first real event from device…
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', overflowY: 'auto', flex: 1 }}>
          {lines.map((raw, i) => {
            const l = downgradeIfSentinel(raw);
            const c = TYPE_COLOR[l.type];
            const isFirst = i === 0;
            const isLast = i === lines.length - 1;
            const tagText = tagLabel(l);
            const time = new Date(l.ts).toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
            return (
              <div key={`${l.type}-${l.ts}-${i}`} style={{ display: 'flex', gap: 11, padding: '9px 0' }}>
                <span
                  className="lde-mono"
                  style={{
                    width: 44,
                    textAlign: 'right',
                    fontSize: 11,
                    color: '#5d7178',
                    flexShrink: 0,
                    paddingTop: 1,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                >
                  {ago(now, l.ts)}
                </span>
                <div style={{ position: 'relative', width: 12, flexShrink: 0 }}>
                  <div
                    style={{
                      position: 'absolute',
                      left: 5,
                      top: isFirst ? 0 : -9,
                      bottom: isLast ? -9 : -9,
                      width: 1,
                      background: 'rgba(120,160,160,0.18)',
                    }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      left: 2,
                      top: 3,
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: c.dot,
                      boxShadow: `0 0 7px ${c.dot}b3`,
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      style={{
                        fontSize: 9.5,
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        color: c.tag,
                        background: c.bordered ? 'transparent' : c.tagBg,
                        border: c.bordered ? `1px solid ${c.dot}66` : 'none',
                        padding: c.bordered ? '1px 5px' : '2px 6px',
                        borderRadius: 4,
                        flexShrink: 0,
                      }}
                    >
                      {tagText}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        color: '#dbe8e8',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {l.headline ?? l.detail ?? l.type}
                    </span>
                  </div>
                  <div
                    className="lde-mono"
                    style={{
                      fontSize: 10,
                      color: '#5d7178',
                      marginTop: 3,
                      fontFamily: "'JetBrains Mono', monospace",
                    }}
                  >
                    {time}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
